import React from "react";
import "./GeneralStatus.css";
import { useBattleStatTooltip } from "./hooks/useBattleStatTooltip";
import {
  BATTLE_HEALTH_BARRIER_THRESHOLDS,
  BARRIER_SPRITE_DISMANTLE_DELAY_MS,
} from "./constants/battleHealthHud";
import { useDelayedBarrierSpriteDisplay } from "./hooks/useDelayedBarrierSpriteDisplay";

// Faction colors for health orbs (matching Strategy.js)
const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  'Ra\'s Children': '#F4A460',
  'Asrah': '#8B0000',
  'The Sentients': '#ADD8E6',
  'Combined': '#FFA500'
};

// Get faction orb background color/gradient (matching Strategy.js implementation)
const getFactionOrbBackground = (factionName) => {
  if (!factionName || factionName === 'Unknown') {
    return '#ccc';
  }

  // Check if it's a combined faction and return gradient (matching Strategy.js exactly)
  if (factionName === 'The Conspiracy') {
    return 'linear-gradient(to right, #8B0000 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Corrupted Core') {
    return 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)';
  } else if (factionName === 'The Covenant') {
    return 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Divine Syndicate') {
    return 'linear-gradient(to right, #F4A460 50%, #4B0082 50%)';
  } else if (factionName === 'The Forsaken Pact') {
    return 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)';
  } else if (factionName === 'The Hollow') {
    return 'linear-gradient(to right, #F4A460 50%, #8B0000 50%)';
  } else if (factionName === 'The Iron Alliance') {
    return 'linear-gradient(to right, #556B2F 50%, #4B0082 50%)';
  } else if (factionName === 'The Last Crusade') {
    return 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)';
  } else if (factionName === 'The Shadow Consortium') {
    return 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)';
  } else if (factionName === 'The Silent Accord') {
    return 'linear-gradient(to right, #556B2F 50%, #1A1A1A 50%)';
  }

  // Return base faction color
  return factionColors[factionName] || '#ccc';
};

const GeneralStatus = ({
  health,
  intelligenceLeft,
  intelligenceTotal,
  barriersRemoved = [],
  damageFlash = false,
  compact = false,
  showIntelligence = true,
  compactIntelligence = false,
  playerCompact = false,
  playerCompactIntelligence = false,
  factionName = null,
  translatedFactionName = null,
  isActiveTurn = false,
  healthDisplayText = null,
  barrierSpriteDelayMs = BARRIER_SPRITE_DISMANTLE_DELAY_MS,
  barrierVisualResetKey = null,
}) => {
  const { isBarrierSpriteDismantled } = useDelayedBarrierSpriteDisplay(
    health,
    barriersRemoved,
    barrierSpriteDelayMs,
    barrierVisualResetKey
  );
  const maxVisualHealth = 30;
  const healthPercent = Math.min(100, (health / maxVisualHealth) * 100);
  const intelligencePercent = Math.max(0, Math.min(100, (intelligenceLeft / intelligenceTotal) * 100));
  const { showStatTooltip, hideStatTooltip, statTooltipPortal } = useBattleStatTooltip();
  const healthOrbDisplay = healthDisplayText != null ? String(healthDisplayText) : String(health);
  const intelOrbDisplay = `${intelligenceLeft}/${intelligenceTotal}`;

  // Player compact mode (health bar on top, health orb on right)
  if (playerCompact) {
    return (
      <>
        {/* Player Compact Health Bar - positioned on top of avatar */}
        <div className={`bar red-bar player-compact-bar ${isActiveTurn ? 'active-turn-glow' : ''}`}>
          <div
            className="red-fill"
            style={{
              width: `${healthPercent}%`,
            }}
          />
          
          {/* Health Threshold Hexagons (barriers) */}
          {BATTLE_HEALTH_BARRIER_THRESHOLDS.map((threshold, index) => {
            const position = (threshold / 30) * 100;
            return (
              <div
                key={threshold}
                className={`hex hex-${index + 1} player-compact-hex`}
                style={{
                  left: `${position}%`,
                  transform: 'translateX(-50%)',
                  opacity: isBarrierSpriteDismantled(threshold) ? 0 : 1,
                  transition: 'opacity 0.3s ease'
                }}
                data-tooltip={`Health threshold: ${threshold}`}
              />
            );
          })}
        </div>

        {/* Player Compact Health Orb - positioned to right of avatar */}
        <div 
          className="circle player-compact-orb"
          data-tooltip={translatedFactionName || factionName || undefined}
          style={{
            background: factionName ? getFactionOrbBackground(factionName) : '#b71c1c',
            border: '2px solid #4a0000',
            boxShadow: factionName ? '0 0 8px rgba(0, 0, 0, 0.4)' : '0 0 8px #b71c1c',
            color: '#ffffff',
            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8)',
            filter: damageFlash ? "brightness(2) saturate(2) hue-rotate(0deg)" : "none",
            transition: "filter 0.1s ease-in-out",
            animation: damageFlash ? "damageFlash 0.5s ease-in-out" : "none"
          }}
        >
          {healthDisplayText != null ? healthDisplayText : health}
        </div>
      </>
    );
  }

  // Player compact intelligence mode (intelligence bar on top, intelligence orb on left)
  if (playerCompactIntelligence) {
    return (
      <>
        {/* Player Compact Intelligence Bar - positioned on top of avatar */}
        <div className="bar blue-bar player-compact-intel-bar">
          <div
            className="blue-fill"
            style={{
              width: `${intelligencePercent}%`,
            }}
          />
        </div>

        {/* Player Compact Intelligence Orb - positioned to left of avatar */}
        <div 
          className="circle blue player-compact-intel-orb"
          style={{
            border: '2px solid #000051',
            boxShadow: '0 0 8px rgba(0, 0, 0, 0.4)',
            color: '#ffffff',
            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8)'
          }}
        >
          {intelligenceLeft}/{intelligenceTotal}
        </div>
      </>
    );
  }

  // Compact intelligence mode for opponent advisor (intelligence orb and bar only)
  if (compactIntelligence) {
    return (
      <>
        {/* Compact Intelligence Orb - positioned to right of avatar */}
        <div
          className="circle blue compact-intel-orb general-status-stat-hit"
          aria-label={`Intelligence: ${intelOrbDisplay}`}
          onMouseEnter={(e) => showStatTooltip(e, intelOrbDisplay)}
          onMouseLeave={hideStatTooltip}
          style={{
            border: '2px solid #000051',
            boxShadow: '0 0 8px rgba(0, 0, 0, 0.4)',
            color: '#ffffff',
            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8)'
          }}
        >
          {intelOrbDisplay}
        </div>

        {/* Compact Intelligence Bar - positioned below avatar */}
        <div className="bar blue-bar compact-intel-bar">
          <div
            className="blue-fill"
            style={{
              width: `${intelligencePercent}%`,
            }}
          />
        </div>
        {statTooltipPortal}
      </>
    );
  }

  // Compact mode for opponent (health orb and bar only)
  if (compact) {
    return (
      <>
        {/* Compact Health Orb - positioned to left of avatar */}
        <div
          className="circle compact-orb general-status-stat-hit"
          aria-label={`Health: ${healthOrbDisplay}`}
          onMouseEnter={(e) => showStatTooltip(e, healthOrbDisplay)}
          onMouseLeave={hideStatTooltip}
          style={{
            background: factionName ? getFactionOrbBackground(factionName) : '#b71c1c',
            border: '2px solid #4a0000',
            boxShadow: factionName ? '0 0 8px rgba(0, 0, 0, 0.4)' : '0 0 8px #b71c1c',
            color: '#ffffff',
            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8)',
            filter: damageFlash ? "brightness(2) saturate(2) hue-rotate(0deg)" : "none",
            transition: "filter 0.1s ease-in-out",
            animation: damageFlash ? "damageFlash 0.5s ease-in-out" : "none"
          }}
        >
          {healthOrbDisplay}
        </div>

        {/* Compact Health Bar - positioned below avatar */}
        <div className={`bar red-bar compact-bar ${isActiveTurn ? 'active-turn-glow' : ''}`}>
          <div
            className="red-fill"
            style={{
              width: `${healthPercent}%`,
            }}
          />
          
          {/* Health Threshold Hexagons (barriers) */}
          {BATTLE_HEALTH_BARRIER_THRESHOLDS.map((threshold, index) => {
            const position = (threshold / 30) * 100;
            return (
              <div
                key={threshold}
                className={`hex hex-${index + 1} compact-hex`}
                style={{
                  left: `${position}%`,
                  transform: 'translateX(-50%)',
                  opacity: isBarrierSpriteDismantled(threshold) ? 0 : 1,
                  transition: 'opacity 0.3s ease'
                }}
                data-tooltip={`Health threshold: ${threshold}`}
              />
            );
          })}
        </div>
        {statTooltipPortal}
      </>
    );
  }

  // Full mode (player's general status)
  return (
    <div className="general-status">
      {/* Health Orb */}
      <div 
        className="circle"
        data-tooltip={translatedFactionName || factionName || undefined}
        style={{
          background: factionName ? getFactionOrbBackground(factionName) : '#b71c1c',
          border: '2px solid #4a0000',
          boxShadow: factionName ? '0 0 8px rgba(0, 0, 0, 0.4)' : '0 0 8px #b71c1c',
          color: '#ffffff',
          textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8)',
          filter: damageFlash ? "brightness(2) saturate(2) hue-rotate(0deg)" : "none",
          transition: "filter 0.1s ease-in-out",
          animation: damageFlash ? "damageFlash 0.5s ease-in-out" : "none"
        }}
      >
        {healthDisplayText != null ? healthDisplayText : health}
      </div>

      {/* Health Bar (Right to Left) */}
      <div className={`bar red-bar ${isActiveTurn ? 'active-turn-glow' : ''}`}>
        <div
          className="red-fill"
          style={{
            width: `${healthPercent}%`,
          }}
        />
        
        {/* Health Threshold Hexagons */}
        {BATTLE_HEALTH_BARRIER_THRESHOLDS.map((threshold, index) => {
          const position = (threshold / 30) * 100;
          return (
            <div
              key={threshold}
              className={`hex hex-${index + 1}`}
              style={{
                left: `${position}%`,
                transform: 'translateX(-50%)',
                opacity: isBarrierSpriteDismantled(threshold) ? 0 : 1,
                transition: 'opacity 0.3s ease'
              }}
              data-tooltip={`Health threshold: ${threshold}`}
            />
          );
        })}
      </div>

      {/* Intelligence Orb */}
      {showIntelligence && (
        <>
          <div 
            className="circle blue"
            style={{
              border: '2px solid #000051',
              boxShadow: '0 0 8px rgba(0, 0, 0, 0.4)',
              color: '#ffffff',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8)'
            }}
          >
            {intelligenceLeft}/{intelligenceTotal}
          </div>

          {/* Intelligence Bar (Left to Right) */}
          <div className="bar blue-bar">
            <div
              className="blue-fill"
              style={{
                width: `${intelligencePercent}%`,
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default GeneralStatus;